import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { Box, Button, Checkbox, CircularProgress, Paper, Typography } from '@material-ui/core'
import { findIndex, head, orderBy } from 'lodash'
import moment from 'moment-timezone'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar } from '@fortawesome/pro-regular-svg-icons'

import { actions as selectedSchoolsActions } from 'store/selected-schools'

import { isCredibleDate } from 'common/date'

import VisitsAvailableCell from './VisitsAvailableCell'
import Table from 'material/MuiTable'
import useTableContext from 'material/MuiTable/use-table-context'

const MAX_SELECTED_SCHOOLS = 3

const HighSchoolsTable = ({
  highSchools,
  totalHighSchools,
  onTableChange,
  onHighSchoolToggle,
  toggledHighSchools,
  onSelectHighSchool,
  loaded,
}) => {
  const [refresh, setRefresh] = useState(false)
  const dispatch = useDispatch()

  const { page, pageSize, sort, sortDir, updateContext } = useTableContext({
    defaultSortKey: 'SchoolName',
    defaultSortDir: 'asc',
  })

  const viewAvailability = (e) => {
    const hsCount = toggledHighSchools.length
    if (hsCount === 0) {
      e.preventDefault()
    } else {
      dispatch(selectedSchoolsActions.setSelectedSchools(toggledHighSchools))
    }
  }
  const toggleHighSchool = (record) => {
    if (toggledHighSchools.length < MAX_SELECTED_SCHOOLS || record.selected) {
      const idx = findIndex(highSchools, (hs) => hs.HighSchoolId === record.HighSchoolId)
      onHighSchoolToggle(idx)
    }
  }
  const disableCheckbox = (record) => {
    if (toggledHighSchools.length >= MAX_SELECTED_SCHOOLS) {
      return record.selected ? false : true
    } else {
      return isCredibleDate(record.HighSchool.NextVisitAvailability) ? false : true
    }
  }
  const getFocus = (selectedSchools) => {
    const firstSchool = head(
      orderBy(
        selectedSchools,
        (school) => {
          return moment(school.NextVisitAvailability).unix()
        },
        ['asc'],
      ),
    )
    if (firstSchool) {
      return moment(firstSchool.NextVisitAvailability).format('YYYYMMDD')
    }
    return ''
  }

  const renderCityState = (highSchool) => {
    return highSchool.City && highSchool.State ? (
      `${highSchool.City}, ${highSchool.State}`
    ) : (
      <>
        <span>&mdash;</span>
      </>
    )
  }
  const renderSchoolType = (highSchool) => {
    return highSchool.SchoolCategory ? (
      `${highSchool.SchoolCategory}`
    ) : (
      <>
        <span>&mdash;</span>
      </>
    )
  }
  const renderSeniorClassSize = (classSize) => {
    return classSize > 0 ? classSize : 'Not Reported'
  }

  useEffect(() => {
    if (refresh) {
      setRefresh(false)
      updateContext({ page: 0 })
    } else {
      onTableChange({
        from: page,
        pageSize,
        sortKey: sort,
        sortDir,
      })
    }
  }, [sort, sortDir, pageSize, page, refresh])

  return (
    <Paper
      component={Box}
      px={2}
      elevation={0}
    >
      {!loaded && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress
            role="status"
            aria-label="loading"
            size={50}
          />
        </Box>
      )}
      {loaded && (
        <Box>
          <Box
            display="flex"
            alignItems="center"
            gridGap={12}
            mb={2}
          >
            <Button
              color="primary"
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faCalendar} />}
              to={`${import.meta.env.BASE_URL}/recruit/visits/view/week?focus=${getFocus(toggledHighSchools)}`}
              onClick={(e) => viewAvailability(e)}
              disabled={toggledHighSchools.length === 0}
              component={Link}
            >
              View Visit Availability
            </Button>
            <Typography>{toggledHighSchools.length} of 3 Selected</Typography>
          </Box>
          <Paper>
            <Table
              records={highSchools}
              keyBy="HighSchoolId"
              columns={[
                {
                  label: 'Actions',
                  id: 'Action',
                  render: (record) => (
                    <Checkbox
                      name={`${record.HighSchool.SchoolName}-select`}
                      id={`${record.HighSchoolId}-checkbox`}
                      onChange={() => toggleHighSchool(record)}
                      checked={record.selected ?? false}
                      color="primary"
                      aria-label={`select ${record.HighSchool.SchoolName}`}
                      disabled={disableCheckbox(record)}
                    />
                  ),
                },
                {
                  label: 'High School',
                  render: (record) => (
                    <Button
                      color="primary"
                      onClick={() => onSelectHighSchool(record.HighSchool)}
                    >
                      {record.HighSchool.SchoolName}
                    </Button>
                  ),
                  sortKey: 'SchoolName',
                  id: 'SchoolName',
                },
                {
                  label: 'Location',
                  render: (record) => <span>{renderCityState(record.HighSchool)}</span>,
                  sortKey: 'City',
                  id: 'City',
                },
                {
                  label: 'Type',
                  render: (record) => <span>{renderSchoolType(record.HighSchool)}</span>,
                  sortKey: 'SchoolCategory',
                  id: 'SchoolCategory',
                },
                {
                  label: 'Senior Class',
                  render: (record) => <span>{renderSeniorClassSize(record.HighSchool.TwelfthGradeStudents)}</span>,
                  sortKey: 'TwelfthGradeStudents',
                  id: 'TwelfthGradeStudents',
                },
                {
                  label: 'Visits Available',
                  render: (record) => (
                    <VisitsAvailableCell
                      highSchoolId={record.HighSchool.Id}
                      highSchoolName={record.HighSchool.SchoolName}
                      visitAvailability={record.HighSchool.NextVisitAvailability}
                    />
                  ),
                  sortKey: 'NextVisitAvailability',
                  id: 'NextVisitAvailability',
                },
              ]}
              total={totalHighSchools}
              order={sortDir}
              orderBy={sort}
              page={page}
              rowsPerPage={pageSize}
              onSortChange={(sort, sortDir) =>
                updateContext({
                  page: 0,
                  sort,
                  sortDir,
                  pageSize,
                })
              }
              onPageSizeChange={(pageSize) =>
                updateContext({
                  page: 0,
                  pageSize,
                })
              }
              onPageChange={(page) => updateContext({ page })}
            />
          </Paper>
        </Box>
      )}
    </Paper>
  )
}

HighSchoolsTable.propTypes = {
  highSchools: PropTypes.array,
  totalHighSchools: PropTypes.number,
  onTableChange: PropTypes.func.isRequired,
  onHighSchoolToggle: PropTypes.func.isRequired,
  toggledHighSchools: PropTypes.array.isRequired,
  onSelectHighSchool: PropTypes.func.isRequired,
  loaded: PropTypes.bool,
}

export default HighSchoolsTable
